import { parseDecimal } from "@bt/parts-core";

/** Tax rates are basis points: 1900 is 19 %, 810 is 8,1 %. */
export const MAX_TAX_RATE_BP = 10_000;

// EXCL_TAX_PER_MINOR, shownPrice and storedPrice are shared with the collector (@bt/parts-core).

/**
 * One entry of a garage's tax rates: the rate from `validFrom` until the next entry starts. The
 * base rate has no start and applies before every change.
 */
export interface TaxRateChange {
  /** First day the rate applies ("YYYY-MM-DD"); null for the base rate. */
  validFrom: string | null;
  rateBp: number;
}

export const DEFAULT_TAX_RATES: readonly TaxRateChange[] = [{ validFrom: null, rateBp: 1900 }];

/** The base rate first, then the changes by date. */
export function sortTaxRates(rates: readonly TaxRateChange[]): TaxRateChange[] {
  return [...rates].sort((a, b) => (a.validFrom ?? "").localeCompare(b.validFrom ?? ""));
}

/** Exactly one base rate, at most one change per day, every rate between 0 and 100 %. */
export function isValidTaxRates(rates: readonly TaxRateChange[]): boolean {
  const dates = new Set<string>();
  let bases = 0;
  for (const { validFrom, rateBp } of rates) {
    if (!Number.isInteger(rateBp) || rateBp < 0 || rateBp > MAX_TAX_RATE_BP) return false;
    if (validFrom === null) bases += 1;
    else if (dates.has(validFrom)) return false;
    else dates.add(validFrom);
  }
  return bases === 1;
}

/** The rate valid on `date`: the latest change starting on or before it, else the base rate. */
export function taxRateOn(rates: readonly TaxRateChange[], date: string): number {
  let rate = 0;
  for (const change of sortTaxRates(rates)) {
    if (change.validFrom !== null && change.validFrom > date) break;
    rate = change.rateBp;
  }
  return rate;
}

/** The calendar day after `date` ("YYYY-MM-DD"). */
export function dayAfter(date: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/**
 * Adds a rate change, or replaces the base rate when `validFrom` is null. With `until`, the change
 * is temporary: the day after it, the rate goes back to what it would have been, and changes
 * inside that time are replaced.
 */
export function addTaxRateChange(
  rates: readonly TaxRateChange[],
  change: { rateBp: number; validFrom: string | null; until?: string | null },
): TaxRateChange[] {
  if (change.validFrom === null) {
    return sortTaxRates([
      ...rates.filter((r) => r.validFrom !== null),
      { validFrom: null, rateBp: change.rateBp },
    ]);
  }
  const from = change.validFrom;
  if (!change.until || change.until < from) {
    return sortTaxRates([
      ...rates.filter((r) => r.validFrom !== from),
      { validFrom: from, rateBp: change.rateBp },
    ]);
  }
  const back = dayAfter(change.until);
  return sortTaxRates([
    ...rates.filter((r) => r.validFrom === null || r.validFrom < from || r.validFrom > back),
    { validFrom: from, rateBp: change.rateBp },
    { validFrom: back, rateBp: taxRateOn(rates, back) },
  ]);
}

/** Removes the change starting on `validFrom`; the base rate stays. */
export function removeTaxRateChange(
  rates: readonly TaxRateChange[],
  validFrom: string,
): TaxRateChange[] {
  return rates.filter((r) => r.validFrom !== validFrom);
}

/**
 * The rate a garage's prices are shown and entered at: none when it shows prices excl. tax, else
 * the order's own rate (a placed order keeps the one it was placed at) or the rate valid today.
 */
export function shownTaxRate(
  settings: { pricesIncludeTax: boolean; taxRates: readonly TaxRateChange[] },
  today: string,
  orderRateBp?: number | null,
): number {
  if (!settings.pricesIncludeTax) return 0;
  return orderRateBp ?? taxRateOn(settings.taxRates, today);
}

/** "19", "8,1" or "7.7 %" as basis points; null unless it's between 0 and 100 %. */
export function parseTaxRate(input: string): number | null {
  const value = parseDecimal(input.replace("%", "").trim());
  if (value === null || value < 0 || value > 100) return null;
  return Math.round(value * 100);
}
